import {
  Avatar,
  Box,
  BoxProps,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  CountryFlag,
  TCountryCode,
  Tooltip,
} from "@govlaunch/web";
import numeral from "numeral";
import React, { ReactNode } from "react";
import { IProductFieldsFragment } from "../fragments/__generated__/ProductFieldsFragment.generated";
import useShowMoreCounter from "../hooks/useShowMoreCounter";
import { FormattedMessage } from "react-intl";

interface IProductGovernmentsProps extends BoxProps {
  product: IProductFieldsFragment;
  renderLinkWrap?: (
    government: NonNullable<NonNullable<IProductFieldsFragment["governments"]>["items"]>[0],
    children: ReactNode,
  ) => ReactNode;
}

export default function ProductGovernments({ product, renderLinkWrap, ...props }: IProductGovernmentsProps) {
  const governments = product.governments?.items || [];
  const totalCount = governments.length;

  const { offset, hasMore, showMore } = useShowMoreCounter(totalCount || 0, 9);
  const items = governments.slice(0, offset) || [];

  if (items.length === 0) {
    return null;
  }

  return (
    <Box bg="white" borderRadius="md" border="1px" borderColor="gray.100" {...props}>
      <Box borderBottom="1px" borderColor="gray.100" py={3} px={8}>
        <Heading as="h3" color="gray.900" fontWeight="bold" fontSize="lg">
          <FormattedMessage defaultMessage="Governments ({totalCount})" id="96+kGo" values={{ totalCount }} />
        </Heading>
      </Box>

      <Box py={3} px={8}>
        <Grid gridTemplateColumns={["1fr", "1fr", "1fr 1fr", "1fr 1fr 1fr"]} gridRowGap={6}>
          {items.map(government => {
            const children = (
              <Flex key={government._id} alignItems="center">
                <Tooltip
                  maxWidth="220px"
                  textAlign="center"
                  aria-label={government.name}
                  label={
                    <span>
                      {government.name} <br /> {government.city.countryName}
                    </span>
                  }
                  bg="blue.600"
                  py={2}
                >
                  <Box position="relative">
                    <Avatar size="lg" src={government.logo} name={government.name} />
                    <Box position="absolute" bottom={1} right={-3}>
                      <CountryFlag countryCode={government.city.country as TCountryCode} width={24} height={14} />
                    </Box>
                  </Box>
                </Tooltip>

                <Box ml={4}>
                  <Box display="flex" alignItems="center">
                    <Text fontWeight={600} fontSize={16}>
                      {government.name}
                    </Text>
                  </Box>

                  {government.city.population !== null && government.city.population > 0 && (
                    <Text as="span" fontSize={12}>
                      {government.city.population !== null && government.city.population > 1000
                        ? numeral(government.city.population).format("0.0a")
                        : government.city.population}{" "}
                      <FormattedMessage defaultMessage="population" id="Hmd2rc" />
                    </Text>
                  )}

                  {government.city.population !== null && government.budget !== null && government.budget > 0 && (
                    <Text as="span"> / </Text>
                  )}

                  {!!government.budget && government.budget > 0 && (
                    <Text as="span" fontSize={12}>
                      {getFormattedBudget(government.budget, government.currency)}
                    </Text>
                  )}
                </Box>
              </Flex>
            );

            return renderLinkWrap ? renderLinkWrap(government, children) : children;
          })}
        </Grid>

        {hasMore && (
          <Flex justify="center" mt={4}>
            <Button
              variant="secondary"
              size="sm"
              onClick={showMore}
              rightIcon={props => (
                <svg
                  width="24"
                  height="12"
                  viewBox="0 0 24 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  {...props}
                >
                  <path
                    d="M2 2.5L11.7436 9.55882L22 2.5"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            >
              <FormattedMessage defaultMessage="Show more" id="aWpBzj" />
            </Button>
          </Flex>
        )}
      </Box>
    </Box>
  );
}

function getFormattedBudget(budget: number, currency: string) {
  const formatted = budget > 1000 ? numeral(budget).format("0.0a") : budget;

  if (currency === "USD") {
    return `$${formatted}`;
  }

  if (currency === "GBP") {
    return `£${formatted}`;
  }

  if (currency === "EUR") {
    return `€${formatted}`;
  }

  if (currency === "CAD") {
    return `CA$${formatted}`;
  }

  if (currency === "AUD") {
    return `AU$${formatted}`;
  }

  if (currency === "NZD") {
    return `NZ$${formatted}`;
  }

  if (currency === "ZAR") {
    return `R${formatted}`;
  }

  if (currency === "BRL") {
    return `R$${formatted}`;
  }

  if (currency === "SEK") {
    return `SEK${formatted}`;
  }

  if (currency === "DKK") {
    return `DKK${formatted}`;
  }

  if (currency === "MXN") {
    return `MX$${formatted}`;
  }

  if (currency === "ARS") {
    return `ARS${formatted}`;
  }

  return `${formatted}`;
}
